
import React from 'react';
import { 
  Package, 
  TrendingUp, 
  Users, 
  DollarSign,
  Truck,
  Clock,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { mockDashboardStats } from '@/api/mockData';

const Dashboard = () => {
  const stats = mockDashboardStats;

  const statsCards = [
    { 
      title: 'Tổng đơn hàng', 
      value: stats.totalOrders.toLocaleString(), 
      change: '+12%', 
      icon: Package, 
      color: 'hsl(var(--primary))' 
    },
    { 
      title: 'Doanh thu hôm nay', 
      value: `${(stats.revenue / 1000000).toFixed(1)}M`, 
      change: '+8%', 
      icon: DollarSign, 
      color: 'hsl(var(--chart-2))' 
    },
    { 
      title: 'Khách hàng', 
      value: stats.customers.toLocaleString(), 
      change: '+3%', 
      icon: Users, 
      color: 'hsl(var(--chart-3))' 
    },
    { 
      title: 'Chuyến đi', 
      value: stats.trips.toString(), 
      change: '+15%', 
      icon: Truck, 
      color: 'hsl(var(--chart-4))' 
    }
  ];

  const recentOrders = [
    { id: 'DH001', customer: 'Nguyễn Văn A', status: 'Đang giao', amount: '250,000', time: '10:30' },
    { id: 'DH002', customer: 'Trần Thị B', status: 'Đã giao', amount: '180,000', time: '09:45' },
    { id: 'DH003', customer: 'Lê Văn C', status: 'Chờ xử lý', amount: '320,000', time: '11:20' },
    { id: 'DH004', customer: 'Phạm Thị D', status: 'Đang giao', amount: '150,000', time: '08:15' }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Đã giao': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Đang giao': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Chờ xử lý': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground">Cập nhật lúc: {new Date().toLocaleString('vi-VN')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsCards.map((stat, index) => (
          <Card key={index} className="animate-slide-in hover:shadow-lg smooth-transition">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div 
                  className="p-3 rounded-lg mr-4"
                  style={{ backgroundColor: `${stat.color}20`, color: stat.color }}
                >
                  <stat.icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <div className="flex items-center space-x-2">
                    <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                    <span className="text-green-600 text-sm font-medium">{stat.change}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <Card className="lg:col-span-2 animate-slide-in">
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-primary" />
              Biểu đồ doanh thu tuần
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.revenueByDay}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Order Status Pie Chart */}
        <Card className="animate-slide-in">
          <CardHeader>
            <CardTitle>Trạng thái đơn hàng</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.ordersByStatus}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {stats.ordersByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              {stats.ordersByStatus.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-muted-foreground">{item.name}</span>
                  </div>
                  <span className="text-sm font-medium">{item.value}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card className="animate-slide-in">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="h-5 w-5 mr-2 text-primary" />
            Đơn hàng gần đây
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2 font-medium text-muted-foreground">Mã đơn</th>
                  <th className="text-left py-3 px-2 font-medium text-muted-foreground">Khách hàng</th>
                  <th className="text-left py-3 px-2 font-medium text-muted-foreground">Trạng thái</th>
                  <th className="text-left py-3 px-2 font-medium text-muted-foreground">Số tiền</th>
                  <th className="text-left py-3 px-2 font-medium text-muted-foreground">Thời gian</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order, index) => (
                  <tr key={index} className="border-b hover:bg-muted/50 smooth-transition">
                    <td className="py-3 px-2 font-medium">{order.id}</td>
                    <td className="py-3 px-2">{order.customer}</td>
                    <td className="py-3 px-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3 px-2 font-medium text-primary">{order.amount}đ</td>
                    <td className="py-3 px-2 text-muted-foreground">{order.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
